import { Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { NgbModal, NgbModalRef } from '@cmps/ng-bootstrap';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { of, Subscription } from 'rxjs';
import { catchError, concatMap, map, tap } from 'rxjs/operators';

import { AuthUser } from '../auth/auth.model';
import { AuthService } from '../auth/auth.service';
import { CommonService, SecurityGroup } from '../common/common.service';
import { Language } from '../i18n/i18n.model';
import { I18nService } from '../i18n/i18n.service';
import {
    UserSecurityGroupAssociation,
    UserSecurityInfo,
} from '../user/user.model';
import { UserService } from '../user/user.service';

import { PasswordProfileComponent } from './password.profile.component';
import { TranslationKeyMap } from './profile.i18n';
import { SecurityQuestionProfileComponent } from './security-question.profile.component';

@Component({
    selector: 'cmps-auth-profile',
    templateUrl: './profile.component.html',
})
export class ProfileComponent implements OnInit, OnDestroy {
    i18n = TranslationKeyMap;
    private _translateSub: Subscription;
    private _loadSub: Subscription;
    hasPendingLanguages: boolean;
    languageOpts: Object[];
    canSelectLanguage: boolean;
    private _photoUrlSub: Subscription;
    photoUrl: SafeResourceUrl | Promise<SafeResourceUrl>;
    hasPendingSecurityInfo: boolean;
    hasPendingSecurityGroups: boolean;
    securityGroupOpts: Object[];
    private _modal: NgbModalRef;

    get user(): AuthUser {
        return this._authService.user;
    }

    get tokenLangDiffers(): boolean {
        try {
            const dec = this._authService.user.token.decodedAccessToken;
            return (
                this._authService.user.languageId !==
                parseInt(dec.payload.langId)
            );
        } catch (err) {}
        return false;
    }

    get hasSaml2AuthMethod(): boolean {
        try {
            return this._authService.user.hasSaml2AuthMethod();
        } catch (err) {}
        return false;
    }

    constructor(
        private readonly _ngZone: NgZone,
        private readonly _domSanitizer: DomSanitizer,
        private readonly _translateService: TranslateService,
        private readonly _authService: AuthService,
        private readonly _modalService: NgbModal,
        private _i18nService: I18nService,
        private readonly _commonService: CommonService,
        private readonly _userService: UserService,
    ) {
        this.languageOpts = [];
        this.canSelectLanguage = true;
        this.securityGroupOpts = [];
    }

    ngOnInit(): void {
        this._translateSub = this._translateService.onLangChange.subscribe(
            (event: LangChangeEvent) => this._load(),
        );

        this._load();

        this.photoUrl = new Promise<SafeResourceUrl>((resolve, reject) => {
            this._photoUrlSub = this._userService
                .retrievePhoto(this.user.id)
                .subscribe(
                    (dataUri) =>
                        resolve(
                            this._domSanitizer.bypassSecurityTrustUrl(dataUri),
                        ),
                    // eslint-disable-next-line n/handle-callback-err, prefer-promise-reject-errors
                    (error) => reject(),
                    // eslint-disable-next-line prefer-promise-reject-errors
                    () => reject(),
                );
        });
    }

    ngOnDestroy(): void {
        if (this._photoUrlSub) {
            this._photoUrlSub.unsubscribe();
        }
        if (this._loadSub) {
            this._loadSub.unsubscribe();
        }
        if (this._translateSub) {
            this._translateSub.unsubscribe();
        }
    }

    private _load(): void {
        this.hasPendingLanguages = true;
        this.hasPendingSecurityInfo = true;
        this.hasPendingSecurityGroups = true;

        if (this._loadSub) {
            this._loadSub.unsubscribe();
        }
        this._loadSub = this._i18nService
            .listLanguages()
            .pipe(
                map((languages: Language[]) => {
                    this.languageOpts = languages.map((value) => {
                        return { label: value.name, value: value.id };
                    });
                }),
                catchError(() => of(null)),
                // eslint-disable-next-line no-return-assign
                tap(() => (this.hasPendingLanguages = false)),
                concatMap(() =>
                    this._userService.retrieveSecurityInfo(this.user.id).pipe(
                        // eslint-disable-next-line no-return-assign
                        map(
                            (securityInfo: UserSecurityInfo) =>
                                (this.user.securityInfo = securityInfo),
                        ),
                        catchError(() => of(null)),
                        // eslint-disable-next-line no-return-assign
                        tap(() => (this.hasPendingSecurityInfo = false)),
                    ),
                ),
                concatMap(() =>
                    this._commonService.listSecurityGroups().pipe(
                        map((securityGroups: SecurityGroup[]) => {
                            this.securityGroupOpts = securityGroups.map(
                                (value) => {
                                    return {
                                        label: value.label,
                                        value: value.id,
                                    };
                                },
                            );
                        }),
                    ),
                ),
                concatMap(() =>
                    this._userService
                        .listSecurityGroupAssociations(this.user.id)
                        .pipe(
                            map(
                                (
                                    securityGroupAssociations: UserSecurityGroupAssociation[],
                                ) => {
                                    this.user.securityGroups =
                                        securityGroupAssociations;
                                },
                            ),
                        ),
                ),
                catchError(() => of(null)),
                // eslint-disable-next-line no-return-assign
                tap(() => (this.hasPendingSecurityGroups = false)),
            )
            // eslint-disable-next-line n/handle-callback-err
            .subscribe(
                () => null,
                // eslint-disable-next-line n/handle-callback-err
                (error) => null,
                () => null,
            );
    }

    onPhotoChange($event): void {
        console.log('onPhotoChange', $event);
        this._userService
            .updatePhoto($event.file, this.user.id)
            .subscribe((result) => null);
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    onLanguageSelect($event) {
        const langId = $event === '' ? null : $event;
        const doSwitch = langId != null && langId !== this.user.languageId;
        const prevLangId = this.user.languageId;
        this.user.languageId = langId;
        if (doSwitch) {
            this.canSelectLanguage = false;
            this._userService
                .update(this.user)
                .pipe(
                    map((result) => {
                        if (result) {
                            this._authService.user.merge(this.user);
                            this._i18nService.locale =
                                this._i18nService.languages.find(
                                    (e) => e.id === langId,
                                ).initial;
                        } else {
                            throw new Error();
                        }
                        return result;
                    }),
                )
                .subscribe(
                    () => null,
                    // eslint-disable-next-line n/handle-callback-err
                    (error) => {
                        this.user.languageId = prevLangId;
                        this.canSelectLanguage = true;
                    },
                    // eslint-disable-next-line no-return-assign
                    () => (this.canSelectLanguage = true),
                );
        }
    }

    changePassword(): void {
        this._modal = this._modalService.open(PasswordProfileComponent);
        // eslint-disable-next-line no-return-assign
        this._modal.result.then(
            (result) => (this._modal = null),
            (reason) => (this._modal = null),
        );
    }

    changeSecurityQuestion(): void {
        this._modal = this._modalService.open(SecurityQuestionProfileComponent);
        this._modal.componentInstance.user = this.user;
        // eslint-disable-next-line no-return-assign
        this._modal.result.then(
            (result) => (this._modal = null),
            (reason) => (this._modal = null),
        );
    }
}
